import React, { useState, useEffect } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import { getAdminById, updateAdmin } from "../../services/admins"
import { ArrowLeftIcon } from "@heroicons/react/24/outline"

const AdminEditUser = () => {
  const { userId } = useParams()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    username: "",
    role: "ADMIN",
    status: 1
  })
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true)
      try {
        const res = await getAdminById(userId)
        if (res.status === 200 && res.data) {
          setFormData({
            name: res.data.name || "",
            phoneNumber: res.data.phoneNumber || "",
            username: res.data.username || "",
            role: res.data.role || "ADMIN",
            status: res.data.status !== undefined ? res.data.status : 1
          })
        } else {
          setError(res.error || 'Failed to load user')
        }
      } catch (err) {
        setError('An unexpected error occurred')
      } finally {
        setLoading(false)
      }
    }

    if (userId) {
      fetchUser()
    }
  }, [userId])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError("")

    const adminPayload = {
      adminId: userId,
      name: formData.name,
      phoneNumber: formData.phoneNumber,
      username: formData.username,
      role: formData.role,
      status: formData.status
    }

    try {
      const res = await updateAdmin(adminPayload)
      if (res.status === 200) {
        navigate('/admin/system-users')
      } else {
        setError(res.error || "Failed to update user")
      }
    } catch (err) {
      setError("Network error")
    }
    setSubmitting(false)
  }

  if (loading) {
    return (
      <div className="max-w-xl mx-auto">
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
          <p className="mt-2 text-slate-500">Loading user...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-xl mx-auto">
      <Link to="/admin/system-users" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6">
        <ArrowLeftIcon className="w-4 h-4" /> Back to Users
      </Link>

      <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Edit System User</h1>

        {error && <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
            <input
              type="text"
              value={formData.username}
              onChange={e => setFormData({ ...formData, username: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
            <input
              type="tel"
              value={formData.phoneNumber}
              onChange={e => setFormData({ ...formData, phoneNumber: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              required
              pattern="[0-9]{11}"
              maxLength={11}
            />
            <p className="text-xs text-slate-500 mt-1">11 digits only</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
            <select
              value={formData.role}
              onChange={e => setFormData({ ...formData, role: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              required
            >
              <option value="ADMIN">Admin</option>
              <option value="SUPER_ADMIN">Super Admin</option>
              <option value="SUPER_MODERATOR">Super Moderator</option>
              <option value="MODERATOR">Moderator</option>
            </select>
            <p className="text-xs text-slate-500 mt-1">
              SUPER_ADMIN: Full access to all modules<br/>
              ADMIN: Limited access to core modules<br/>
              SUPER_MODERATOR: Can view all, limited edit<br/>
              MODERATOR: Read-only access
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={e => setFormData({ ...formData, status: parseInt(e.target.value) })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              required
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-3 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition disabled:opacity-50"
            >
              {submitting ? 'Updating...' : 'Update User'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/system-users')}
              className="px-6 py-3 bg-white text-slate-700 font-medium rounded-xl border border-slate-300 hover:bg-slate-50 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AdminEditUser

